import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Mail, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { format } from "date-fns";

interface EmailLog {
  id: string;
  recipient_email: string;
  email_type: string;
  subject: string | null;
  status: string;
  error_message: string | null;
  sent_at: string;
  metadata: Record<string, unknown> | null;
}

interface Props {
  /** Client's auth user_id (preferred) */
  userId: string;
  /** Client's email — used as a fallback when older logs don't have a user_id */
  email?: string | null;
}

const TYPE_LABELS: Record<string, string> = {
  booking_confirmation: "Booking confirmation",
  booking_cancellation: "Booking cancellation",
  booking_completion: "Thank-you email",
  booking_change_request: "Change request",
  booking_reminder: "Appointment reminder",
  payment_receipt: "Payment receipt",
  credit_issued: "Credit issued",
  coupon_expiry_reminder: "Credit expiring soon",
};

const prettyType = (t: string) => TYPE_LABELS[t] ?? t.replace(/_/g, " ");

const ClientEmailHistory = ({ userId, email }: Props) => {
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);

      // Match by user_id first; older rows without a user_id link can still be
      // surfaced by recipient email so admins see the full picture.
      const orFilter = email
        ? `user_id.eq.${userId},recipient_email.ilike.${email}`
        : `user_id.eq.${userId}`;

      const { data, error } = await supabase
        .from("email_logs")
        .select("id, recipient_email, email_type, subject, status, error_message, sent_at, metadata")
        .or(orFilter)
        .order("sent_at", { ascending: false })
        .limit(50);

      if (cancelled) return;
      if (error) {
        console.error("Failed to load email logs:", error.message);
        setLogs([]);
      } else {
        setLogs((data || []) as EmailLog[]);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, email]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Mail size={18} /> Email History ({logs.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : logs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No emails have been sent to this client yet.</p>
        ) : (
          <div className="space-y-2">
            {logs.map((log) => {
              const failed = log.status !== "sent";
              return (
                <div
                  key={log.id}
                  className="flex items-start justify-between gap-3 p-3 rounded-lg bg-muted/30 border border-border/30"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium truncate">
                        {log.subject || prettyType(log.email_type)}
                      </p>
                      <Badge
                        variant={failed ? "destructive" : "secondary"}
                        className="text-[10px] py-0 px-1.5 gap-1"
                      >
                        {failed ? <AlertCircle size={10} /> : <CheckCircle2 size={10} />}
                        {failed ? "Failed" : "Sent"}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {prettyType(log.email_type)} · {log.recipient_email}
                    </p>
                    {failed && log.error_message && (
                      <p className="text-xs text-destructive mt-1 truncate">{log.error_message}</p>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground whitespace-nowrap">
                    {format(new Date(log.sent_at), "MMM d, h:mm a")}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ClientEmailHistory;
